import AddIcon from '@mui/icons-material/Add';
import DnsIcon from '@mui/icons-material/Dns';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppModal } from '../../../../components/ui/AppModal';
import { useFetch } from '../../../../hooks/useFetch';
import { isValidEmail } from '../../../../lib/validation';
import { InstanceApiFactory } from '../../api/instanceApi';
import { useGetTerrains } from '../../hooks/useGetTerrains';
import { getTerrainLabel, isSameTerrain } from '../../utils/terrain';
import type { Terrain } from '../../instanceTypes';

interface NewInstanceModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function NewInstanceModal({ open, onClose, onCreated }: NewInstanceModalProps) {
  const { t } = useTranslation('instances');
  const [client] = useFetch();
  const api = useMemo(() => InstanceApiFactory(client), [client]);
  const { data: terrainOptions, loading: terrainsLoading } = useGetTerrains();

  const [instanceName, setInstanceName] = useState('');
  const [email, setEmail] = useState('');
  const [terrains, setTerrains] = useState<Terrain[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInstanceName('');
      setEmail('');
      setTerrains([]);
      setInputValue('');
      setTouched({});
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const errors = useMemo(
    () => ({
      instanceName: instanceName.length > 255 ? t('create.errors.instanceNameTooLong') : null,
      email: !email.trim()
        ? t('create.errors.emailRequired')
        : !isValidEmail(email)
          ? t('create.errors.emailInvalid')
          : null,
      terrains: terrains.length === 0 ? t('create.errors.terrainsRequired') : null,
    }),
    [instanceName, email, terrains, t],
  );

  const hasErrors = Object.values(errors).some(Boolean);

  const touch = (field: string) => setTouched(prev => ({ ...prev, [field]: true }));

  const handleSubmit = async () => {
    setTouched({ instanceName: true, email: true, terrains: true });
    if (hasErrors) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.create({
        instance_name: instanceName.trim() || undefined,
        email: email.trim(),
        codeterrain: [...new Set(terrains.map(t => t.codeterrain))],
      });
      onCreated();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('create.errors.createFailed'));
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      disableClose={submitting}
      titleId="new-inst-title"
      icon={<DnsIcon sx={theme => ({ fontSize: theme.iconSize.sm })} />}
      title={t('create.title')}
      subtitle={t('create.subtitle')}
      closeLabel={t('create.cancel')}
      // Cap the modal height and let the content scroll, so the action buttons
      // (Cancel / Create) stay pinned and visible even when many terrains are
      // selected (HOMCO-1114).
      paperSx={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
    >
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          py: 2.5,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {/* Instance name (optional) */}
        <TextField
          label={t('create.instanceName.label')}
          size="small"
          value={instanceName}
          onChange={e => setInstanceName(e.target.value)}
          onBlur={() => touch('instanceName')}
          error={!!(touched.instanceName && errors.instanceName)}
          helperText={touched.instanceName ? errors.instanceName : undefined}
          placeholder={t('create.instanceName.placeholder')}
          slotProps={{
            htmlInput: { 'aria-label': t('create.instanceName.label'), maxLength: 255 },
          }}
        />

        {/* Email (required) */}
        <TextField
          label={t('create.email.label')}
          size="small"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          onBlur={() => touch('email')}
          error={!!(touched.email && errors.email)}
          helperText={touched.email ? errors.email : undefined}
          placeholder={t('create.email.placeholder')}
          type="email"
          slotProps={{ htmlInput: { 'aria-label': t('create.email.label') } }}
        />

        {/* Terrains (required) */}
        <Box>
          <Typography
            variant="body2"
            sx={theme => ({ fontWeight: theme.typography.fontWeightSemiBold, mb: 0.75 })}
          >
            {t('create.terrains.label')}
            {' *'}
            {terrains.length > 0 && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={th => ({ ml: 1, fontWeight: th.typography.fontWeightRegular })}
              >
                ({terrains.length})
              </Typography>
            )}
          </Typography>
          <Autocomplete<Terrain, true, false, false>
            multiple
            // Bound the selected-chips area so a large selection scrolls inside the
            // field instead of growing the modal (HOMCO-1114).
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                maxHeight: 180,
                overflowY: 'auto',
                alignContent: 'flex-start',
              },
            }}
            options={terrainOptions}
            value={terrains}
            inputValue={inputValue}
            onInputChange={(_, v) => setInputValue(v)}
            // HOMCO-1398: no free-text code — the selection is always a known Terrain
            // from the options (terrain writes are admin-only, no ad-hoc creation here).
            onChange={(_, newVal) => setTerrains(newVal)}
            loading={terrainsLoading}
            disableCloseOnSelect
            getOptionLabel={getTerrainLabel}
            isOptionEqualToValue={isSameTerrain}
            slotProps={{
              paper: {
                sx: theme => ({
                  color: theme.palette.text.body,
                  '& .MuiAutocomplete-option': { color: theme.palette.text.body },
                }),
              },
              chip: {
                size: 'small',
                sx: theme => ({
                  fontWeight: theme.typography.fontWeightSemiBold,
                  fontSize: theme.typography.caption.fontSize,
                  height: theme.chip.sm,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.dark,
                  '& .MuiChip-deleteIcon': { color: theme.palette.primary.dark },
                }),
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder={
                  terrains.length === 0 && !inputValue ? t('create.terrains.placeholder') : ''
                }
                error={!!(touched.terrains && errors.terrains)}
                helperText={
                  touched.terrains && errors.terrains ? errors.terrains : t('create.terrains.hint')
                }
                onBlur={() => touch('terrains')}
              />
            )}
          />
        </Box>

        {/* Submit error */}
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            <Typography
              variant="body2"
              gutterBottom
              sx={theme => ({ fontWeight: theme.typography.fontWeightSemiBold })}
            >
              {t('create.errors.createFailed')}
            </Typography>
            <Typography variant="caption">{submitError}</Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        <Box />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={submitting}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('create.cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={submitting}
            aria-busy={submitting}
            startIcon={
              submitting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <AddIcon />
            }
            sx={{ borderRadius: 5, textTransform: 'none', minWidth: 140 }}
          >
            {submitting ? t('create.submitting') : t('create.submit')}
          </Button>
        </Box>
      </DialogActions>
    </AppModal>
  );
}
